import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ClerkAuthProvider, AuthProvider, useAuth } from "./contexts/ClerkAuthContext";
import { NavbarProvider, useNavbar } from "./contexts/NavbarContext";
import { SignIn } from "./components/Auth/SignIn";
import { SignUp } from "./components/Auth/SignUp";
import { Navbar } from "./components/Navigation/Navbar";
import { HomePage } from "./components/Home/HomePage";
import { SettingsPage } from "./components/Settings/SettingsPage";
import { AdminPage } from "./components/Admin/AdminPage";
import { UnauthorizedPage } from "./components/Common/UnauthorizedPage";
import { apiService } from "./services/api";
import { AppEditor } from "./components/AppEditor/AppEditor";
import { EvalExecution } from "./components/Admin/EvalExecution";
import { DevboxAuthPage } from "./pages/DevboxAuth";
import { ErrorPanel } from "./components/Errors/ErrorPanel";
import { ExplorationPage } from "./components/Exploration/ExplorationPage";

const App: React.FC = () => {
  return (
    <ClerkAuthProvider>
      <AuthProvider>
        <NavbarProvider>
          <Router>
            <AppRoutes />
          </Router>
        </NavbarProvider>
      </AuthProvider>
    </ClerkAuthProvider>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOrg?: boolean;
}

function ProtectedRoute({ children, requireOrg = false }: ProtectedRouteProps) {
  const { isAuthenticated, organization, internalOrgId, isOrgLoading } = useAuth();
  const hasActiveOrg = isAuthenticated && (organization || internalOrgId);

  if (!isAuthenticated) {
    return <Navigate to="/signin" />;
  }

  if (requireOrg && !hasActiveOrg) {
    // Always show loading while org is being set up or if we haven't determined org status yet
    // This prevents premature redirects during page refresh
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Setting up your account...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, isLoading, isOrgLoading, error } = useAuth();
  const { applicationName } = useNavbar();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Set up the unauthorized callback to navigate to /unauthorized
    apiService.setUnauthorizedCallback(() => {
      navigate('/unauthorized');
    });
  }, [navigate]);

  // Show loading while auth is loading OR while we're fetching org data for authenticated users
  if (isLoading || (isAuthenticated && isOrgLoading)) {
    // Don't show navbar on devbox auth route
    if (location.pathname === '/auth/devbox') {
      return <div className="h-screen bg-gray-50"></div>;
    }
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <main className="flex-1"></main>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <Routes>
        {/* DevboxAuth route without navbar */}
        <Route
          path="/auth/devbox"
          element={
            <ProtectedRoute>
              <DevboxAuthPage />
            </ProtectedRoute>
          }
        />

        {/* All other routes with navbar */}
        <Route
          path="*"
          element={
            <>
              <Navbar applicationName={applicationName || undefined} />
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                  <div className="max-w-7xl mx-auto">
                    {error}
                  </div>
                </div>
              )}
              <main className={`flex-1 ${(location.pathname.includes('/applications/') && location.pathname.includes('/edit')) || location.pathname.includes('/explore/') ? 'overflow-hidden' : 'overflow-auto'}`}>
                <Routes>
                  <Route
                    path="/signin"
                    element={!isAuthenticated ? <SignIn /> : <Navigate to="/" />}
                  />
                  <Route
                    path="/signup"
                    element={!isAuthenticated ? <SignUp /> : <Navigate to="/" />}
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute requireOrg>
                        <HomePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute requireOrg>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/evals"
                    element={
                      <ProtectedRoute>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/pool"
                    element={
                      <ProtectedRoute>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/evals/:id/execute"
                    element={
                      <ProtectedRoute>
                        <EvalExecution />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/applications/:id/edit"
                    element={
                      <ProtectedRoute requireOrg>
                        <AppEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/explore/:id"
                    element={
                      <ProtectedRoute requireOrg>
                        <ExplorationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/errors"
                    element={
                      <ProtectedRoute requireOrg>
                        <ErrorPanel />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/unauthorized"
                    element={<UnauthorizedPage />}
                  />
                </Routes>
              </main>
            </>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
